import { InboundEmailDTO } from '@/types/application/InboundEmailDTO';
import { EmailData } from './email-viewer-types';
import type { InboundEmail } from '@erp/rpc';

// Helper function to determine if a file is an Excel file
export const isExcelFile = (attachment: {
  fileName?: string;
  mimeType?: string;
}) => {
  const fileName = attachment.fileName?.toLowerCase();
  const excelMimeTypes = [
    'application/excel',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
  ];

  return (
    excelMimeTypes.includes(attachment.mimeType || '') ||
    fileName?.endsWith('.xlsx') ||
    fileName?.endsWith('.xls') ||
    fileName?.endsWith('.csv') ||
    fileName?.endsWith('.ods')
  );
};

// Helper function to determine if a file is a PDF
export const isPdfFile = (attachment: {
  fileName?: string;
  mimeType?: string;
}): boolean => {
  const fileName = attachment.fileName?.toLowerCase();
  return (
    attachment.mimeType === 'application/pdf' || !!fileName?.endsWith('.pdf')
  );
};

// True for the "document" attachments (PDFs / spreadsheets) — used to hide
// inline images (e.g. signature logos) from document-centric attachment lists.
export const isPdfOrExcelAttachment = (attachment: {
  fileName?: string;
  mimeType?: string;
}): boolean => isPdfFile(attachment) || !!isExcelFile(attachment);

// Type guard to check if email is InboundEmail (RPC type)
function isInboundEmail(
  email: InboundEmailDTO | InboundEmail,
): email is InboundEmail {
  return (
    'attachments' in email &&
    email.attachments?.[0] !== undefined &&
    'fileName' in email.attachments[0]
  );
}

export function inboundEmailToEmailData(
  inboundEmail: InboundEmailDTO | InboundEmail,
): EmailData {
  // Check if this is RPC InboundEmail type (has attachments with fileName instead of originalName)
  if (isInboundEmail(inboundEmail)) {
    return {
      inboundEmailId: inboundEmail.id,
      subject: inboundEmail.subject ?? undefined,
      from: inboundEmail.from,
      to: inboundEmail.to,
      cc: inboundEmail.cc ?? undefined,
      htmlContent: inboundEmail.htmlContent ?? undefined,
      textContent: inboundEmail.textContent ?? undefined,
      attachments: inboundEmail.attachments?.map((attachment) => ({
        id: attachment.id,
        fileName: attachment.fileName,
        sizeBytes: attachment.sizeBytes ?? 0,
        mimeType: attachment.mimeType ?? '',
        s3Url: attachment.s3Url,
        contentId: attachment.contentId ?? null,
      })),
      date: inboundEmail.createdAt
        ? new Date(inboundEmail.createdAt).toISOString()
        : undefined,
    };
  }

  // Handle InboundEmailDTO type
  return {
    inboundEmailId: inboundEmail.id,
    subject: inboundEmail.subject ?? undefined,
    from: inboundEmail.from,
    to: inboundEmail.to,
    cc: inboundEmail.cc ?? undefined,
    htmlContent: inboundEmail.htmlContent ?? undefined,
    textContent: inboundEmail.textContent ?? undefined,
    attachments: inboundEmail.attachments?.map((attachment) => ({
      id: attachment.id,
      fileName: attachment.originalName,
      sizeBytes: attachment.sizeBytes,
      mimeType: attachment.mimeType,
      s3Url: attachment.s3Url,
      contentId: attachment.contentId ?? null,
    })),
    splitAttachmentsCount: inboundEmail.splitAttachmentsCount,
    splitAttachments: inboundEmail.splitAttachments?.map((attachment) => ({
      id: attachment.id,
      fileName: attachment.originalName,
      sizeBytes: attachment.sizeBytes,
      mimeType: attachment.mimeType,
      s3Url: attachment.s3Url,
      contentId: attachment.contentId ?? null,
    })),
    date: inboundEmail.createdAt
      ? new Date(inboundEmail.createdAt).toISOString()
      : undefined,
  };
}
